import { useEffect, useRef, useState, type FormEvent } from "react";
import { CoffreDenied, coffreUnlockClosed, onCoffreUnlockAsk, requestCoffreUnlock, unlockCoffre } from "./coffre";
import { Modal } from "./Modal";

export function useCoffreUnlocked(retry: () => void) {
  const retryRef = useRef(retry);
  retryRef.current = retry;
  useEffect(() => {
    const run = () => retryRef.current();
    window.addEventListener("crm-coffre-unlocked", run);
    return () => window.removeEventListener("crm-coffre-unlocked", run);
  }, []);
}

export function CoffreUnlockHost() {
  const [open, setOpen] = useState(false);
  useEffect(() => onCoffreUnlockAsk(() => setOpen(true)), []);
  if (!open) return null;
  return (
    <Modal
      onClose={() => {
        coffreUnlockClosed();
        setOpen(false);
      }}
      sheetClass="crm-pop w-full max-w-md rounded-3xl border border-slate-200 p-6 shadow-2xl"
    >
      {(close) => <CoffreUnlockForm onDone={close} />}
    </Modal>
  );
}

function CoffreUnlockForm({ onDone }: { onDone: () => void }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [fail, setFail] = useState("");
  const [stopped, setStopped] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (code.trim() === "" || busy || stopped) return;
    setBusy(true);
    setFail("");
    try {
      await unlockCoffre(code);
      setCode("");
      onDone();
    } catch (reason: unknown) {
      setFail(reason instanceof Error ? reason.message : "Code refusé.");
      setCode("");
      if (reason instanceof CoffreDenied && reason.code === "coffre_suite_locked") setStopped(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={(event) => void submit(event)}>
      <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Coffre</p>
      <h2 className="mt-1 flex items-center gap-2 text-lg font-semibold">
        <LockIcon />
        Mot de passe maître
      </h2>
      <p className="mt-2 text-sm leading-relaxed text-slate-600">Saisissez votre mot de passe maître pour accéder à vos dossiers et fichiers. Il n’est pas enregistré ici.</p>
      <input
        type="password"
        name="coffre-unlock-pass"
        autoComplete="off"
        autoFocus
        value={code}
        disabled={busy || stopped}
        onChange={(event) => setCode(event.target.value)}
        placeholder="Mot de passe maître"
        className="crm-field mt-4 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
      />
      {fail ? <p className="mt-2 text-sm text-rose-700">{fail}</p> : null}
      <div className="mt-4 flex justify-end">
        <button type="submit" disabled={busy || stopped || code.trim() === ""} className="rounded-full crm-fill px-4 py-1.5 text-sm font-semibold text-white disabled:opacity-50">
          {busy ? "Vérification…" : "Accéder au Coffre"}
        </button>
      </div>
    </form>
  );
}

export function CoffreLockedLine({ className = "mb-2 text-sm text-slate-600" }: { className?: string }) {
  return (
    <p className={className}>
      <span className="inline-flex items-center gap-1.5">
        <LockIcon />
        Le Coffre est fermé.
      </span>{" "}
      <button type="button" className="font-semibold underline" onClick={() => requestCoffreUnlock()}>Saisir le mot de passe maître</button>
    </p>
  );
}

function LockIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
